import React, { useState, useRef, useCallback, ChangeEvent, FocusEvent, KeyboardEvent } from 'react'
import { useI18n } from 'builder/hooks/useI18n'
import { EditorTitleProps } from './types'
import {
  CompactContainer,
  CompactTextDisplay,
  CompactInput,
  CompactTooltip,
  Pencil,
} from './styles'
import PENCIL from './pencil.svg'

const EditorTitleCompact: React.FC<EditorTitleProps> = ({ value, onChange, onEditingChange }) => {
  const [inputValue, setInputValue] = useState(value)
  const [isEditing, setIsEditing] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { i18n } = useI18n()

  const handleEditClick = useCallback(() => {
    setIsEditing(true)
    onEditingChange?.(true)
    setShowTooltip(false)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
        inputRef.current.select()
      }
    }, 0)
  }, [onEditingChange])

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }, [])

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsEditing(false)
      onEditingChange?.(false)
      const trimmedValue = e.target.value.trim()
      onChange(trimmedValue)
    },
    [onChange, onEditingChange],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (inputRef.current) {
          inputRef.current.blur()
        }
      }
      if (e.key === 'Escape') {
        setInputValue(value)
        setIsEditing(false)
        onEditingChange?.(false)
      }
    },
    [value, onEditingChange],
  )

  const handleMouseEnter = useCallback(() => {
    if (!isEditing) {
      setShowTooltip(true)
    }
  }, [isEditing])

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false)
  }, [])

  return (
    <CompactContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      $isHovered={showTooltip}
    >
      {isEditing ? (
        <CompactInput
          ref={inputRef}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={i18n.t('builder.resume_editor.untitled')}
          size={inputValue.length}
        />
      ) : (
        <>
          <CompactTextDisplay $isHovered={showTooltip}>{value}</CompactTextDisplay>
          <Pencil onClick={handleEditClick} $icon={PENCIL} />
          {showTooltip && <CompactTooltip>{i18n.t('builder.dashboard.rename')}</CompactTooltip>}
        </>
      )}
    </CompactContainer>
  )
}

export default EditorTitleCompact
